import {
  deriveCurrentLoomDependentAncestry,
  type LoomAuthoringHistory,
  type LoomAuthoringOperation,
} from "./loomAuthoringHistory";
import { loomHistoryUserUndoFloor, type LoomHistoryStorageStatus } from "./loomAuthoringHistoryStorage";
import type { LoomControlWorkspaceHistory, LoomControlWorkspaceOperation } from "./loomControlWorkspaceHistory";
import type { LoomPersistenceWriteState } from "./useLoomAuthoringHistory";

interface LoomOperationHistoryProps {
  history: LoomAuthoringHistory;
  onRedo: () => void;
  onUndo: () => void;
  onWorkspaceRedo: () => void;
  onWorkspaceUndo: () => void;
  persistenceWriteState: LoomPersistenceWriteState;
  storageStatus: LoomHistoryStorageStatus;
  workspaceHistory: LoomControlWorkspaceHistory;
}

function shortId(value: string): string {
  return value.length <= 18 ? value : `${value.slice(0, 10)}…${value.slice(-6)}`;
}

function operationOrigin(operation: LoomAuthoringOperation): string {
  return operation.kind === "command.accepted" ? operation.origin : `history.${operation.move}`;
}

function operationStates(operation: LoomAuthoringOperation): { from: string; to: string } {
  return operation.kind === "command.accepted"
    ? { from: operation.parent.stateId, to: operation.result.stateId }
    : { from: operation.from.stateId, to: operation.to.stateId };
}

function workspaceOperationOrigin(operation: LoomControlWorkspaceOperation): string {
  return operation.kind === "intent.accepted" ? operation.origin : `workspace.${operation.move}`;
}

function patternStep(operation: LoomAuthoringOperation): string {
  if (operation.kind === "head.moved") return operation.move === "undo" ? "Move head back" : "Move head forward";
  const command = operation.command;
  if (command.kind === "node.add") return `Add free ${command.node.label}`;
  if (command.kind === "node.attach") return command.attachment.kind === "new-root"
    ? `Add output ${command.node.label}`
    : command.node.type === "boolean" ? `Boolean ${command.node.label}` : `Wrap ${command.node.label}`;
  if (command.kind === "mirror.partner.create") return `Mirror ${command.sourceNodeId} as ${command.partner.nodeId}`;
  if (command.kind === "mirror.partner.remove") return `Unmirror ${command.sourceNodeId}`;
  if (command.kind === "node.remove") return `Remove ${command.nodeId}`;
  if (command.kind === "node.rename") return `Rename ${command.nodeId}`;
  if (command.kind === "primitive.replace") return `Edit ${command.nodeId}`;
  if (command.kind === "transform.replace") return `Transform ${command.nodeId}`;
  if (command.kind === "boolean.replace") return `${command.operation} ${command.nodeId}`;
  if (command.kind === "import.replace") return `Update import ${command.nodeId}`;
  if (command.kind === "nodes.reorder") return "Reorder definition";
  if (command.kind === "roots.set") return "Set Pattern outputs";
  return "Upgrade legacy Pattern";
}

function workspaceStep(operation: LoomControlWorkspaceOperation): string {
  if (operation.kind === "head.moved") return operation.move === "undo" ? "Undo workspace step" : "Redo workspace step";
  return operation.intent.kind === "controls.pin" ? "Pin controls" : "Define design goal";
}

export function LoomOperationHistory({
  history,
  onRedo,
  onUndo,
  onWorkspaceRedo,
  onWorkspaceUndo,
  persistenceWriteState,
  storageStatus,
  workspaceHistory,
}: LoomOperationHistoryProps) {
  const dependent = deriveCurrentLoomDependentAncestry(history);
  const dependentIds = new Set(dependent.commandOperationIds);
  const activeBranchIds = new Set(history.activeBranchOperationIds);
  const undoFloor = loomHistoryUserUndoFloor(history, storageStatus);
  const readOnly = storageStatus.kind === "legacy-v1-retained" || storageStatus.kind === "storage-blocked";
  const canUndo = !readOnly && history.cursor > undoFloor;
  const canRedo = !readOnly && history.cursor < history.activeBranchOperationIds.length;
  const workspaceBranchIds = new Set(workspaceHistory.activeBranchOperationIds);
  const canWorkspaceUndo = !readOnly && workspaceHistory.cursor > 0;
  const canWorkspaceRedo = !readOnly && workspaceHistory.cursor < workspaceHistory.activeBranchOperationIds.length;
  return (
    <section
      className="loom-operation-history"
      data-audit-count={history.operations.length}
      data-total-audit-count={history.operations.length + workspaceHistory.operations.length}
      data-workspace-audit-count={workspaceHistory.operations.length}
      data-workspace-bootstrap={workspaceHistory.bootstrap.kind}
      data-workspace-cursor={workspaceHistory.cursor}
      data-state-count={history.states.length}
      data-storage-status={storageStatus.kind}
      data-persistence-write-state={persistenceWriteState}
      data-undo-floor={undoFloor}
      data-testid="loom-operation-history"
    >
      <div className="loom-history-toolbar">
        <div>
          <span>Loom Pattern</span>
          <strong>{dependent.commandOperationIds.length} current-path · {history.operations.length} geometry · {workspaceHistory.operations.length} workspace steps retained</strong>
        </div>
        <span>
          <button aria-label="Undo" data-testid="loom-history-undo" disabled={!canUndo} onClick={onUndo} type="button">↶</button>
          <button aria-label="Redo" data-testid="loom-history-redo" disabled={!canRedo} onClick={onRedo} type="button">↷</button>
        </span>
      </div>
      <div className="loom-history-head">
        <span>Pattern head</span>
        <code title={history.activeHead.stateId}>{shortId(history.activeHead.stateId)}</code>
        <small>r{history.activeHead.revision} · returning to identical content reuses this state</small>
      </div>
      <div className="loom-history-head" data-testid="loom-workspace-history-head">
        <span>Controls and goals · linked to exact geometry states</span>
        <code title={workspaceHistory.activeHead.stateId}>{shortId(workspaceHistory.activeHead.stateId)}</code>
        <span>
          <button aria-label="Undo workspace change" data-testid="loom-workspace-history-undo" disabled={!canWorkspaceUndo} onClick={onWorkspaceUndo} type="button">↶</button>
          <button aria-label="Redo workspace change" data-testid="loom-workspace-history-redo" disabled={!canWorkspaceRedo} onClick={onWorkspaceRedo} type="button">↷</button>
        </span>
      </div>
      {workspaceHistory.bootstrap.kind === "geometry-only-v2-migrated" ? (
        <small data-testid="loom-workspace-storage-upgrade" role="status">Recognized the prior geometry-only v2 envelope · geometry audit preserved · empty workspace audit initialized by serialized compare-and-set.</small>
      ) : null}
      <details>
        <summary>Pattern steps and current path</summary>
        <div className="loom-history-scroll">
          <ol aria-label="Loom Pattern geometry steps">
            {history.operations.map((operation) => {
              const states = operationStates(operation);
              const onDependentPath = dependentIds.has(operation.operationId);
              const branchStatus = operation.kind === "head.moved"
                ? "audit-only"
                : onDependentPath
                  ? "dependent"
                  : activeBranchIds.has(operation.operationId)
                    ? "redo-or-loop"
                    : "alternate";
              return (
                <li
                  data-branch-status={branchStatus}
                  data-from-content-id={states.from}
                  data-kind={operation.kind}
                  data-on-dependent-path={onDependentPath}
                  data-operation-id={operation.operationId}
                  data-origin={operationOrigin(operation)}
                  data-testid="loom-operation-row"
                  data-to-content-id={states.to}
                  key={operation.operationId}
                >
                  <span title={operationOrigin(operation)}><b>#{operation.sequence + 1}</b>{patternStep(operation)}</span>
                  <code>{shortId(states.from)} → {shortId(states.to)}</code>
                  <em>{branchStatus}</em>
                </li>
              );
            })}
          </ol>
          <ol aria-label="Loom Pattern control and goal steps">
            {workspaceHistory.operations.map((operation) => {
              const accepted = operation.kind === "intent.accepted";
              const from = accepted ? operation.parent.stateId : operation.from.stateId;
              const to = accepted ? operation.result.stateId : operation.to.stateId;
              const branchIndex = accepted ? workspaceHistory.activeBranchOperationIds.indexOf(operation.operationId) : -1;
              const branchStatus = !accepted
                ? "audit-only"
                : !workspaceBranchIds.has(operation.operationId)
                  ? "alternate"
                  : branchIndex < workspaceHistory.cursor ? "active" : "redo";
              return (
                <li
                  data-branch-status={branchStatus}
                  data-from-content-id={from}
                  data-kind={operation.kind}
                  data-operation-id={operation.operationId}
                  data-origin={workspaceOperationOrigin(operation)}
                  data-testid="loom-workspace-operation-row"
                  data-to-content-id={to}
                  key={operation.operationId}
                >
                  <span title={workspaceOperationOrigin(operation)}><b>W#{operation.sequence + 1}</b>{workspaceStep(operation)}</span>
                  <code>{shortId(from)} → {shortId(to)}</code>
                  <em>{branchStatus}</em>
                </li>
              );
            })}
          </ol>
          <div data-testid="loom-dependent-path" className="loom-dependent-path">
            <span>Current path</span>
            {dependent.commandOperationIds.length === 0 ? <small>Initial state · no dependent steps</small> : dependent.commandOperationIds.map((operationId) => (
              <code data-testid="loom-dependent-operation" key={operationId}>{shortId(operationId)}</code>
            ))}
          </div>
        </div>
      </details>
      <small className="loom-history-boundary" data-testid="loom-history-storage-boundary">
        {storageStatus.kind === "storage-blocked" || persistenceWriteState === "blocked"
          ? `Memory-only Pattern · browser persistence blocked (${storageStatus.kind === "storage-blocked" ? storageStatus.reason : "write-failed"}). Export or repair storage before relying on this history.`
          : persistenceWriteState === "pending"
            ? "Saving Pattern in browser… waiting for the exact geometry + workspace envelope to pass serialized compare-and-set."
          : storageStatus.kind === "legacy-v1-retained"
            ? "Persisted browser-locally in v2 · legacy v1 retained untouched · automatic graph migration blocked. Clearing site data can remove the v2 copy."
            : storageStatus.kind === "legacy-default-migrated"
              ? "Persisted browser-locally in v2 · legacy default upgraded by one audited migration. Clearing site data can remove it."
              : "Persisted browser-locally in v2. Clearing site data or browser policy can remove it; canonical Thread V3 persistence is the next boundary."}
      </small>
    </section>
  );
}
